/**
 * anchorResolver — classifies a workflow-tier user request into one of
 * three outputs, gating the LLM call:
 *
 *   { status: 'anchored', protein, source }
 *     The request names a target the resolver can verify.
 *
 *   { status: 'ambiguous', domainTerms[] }
 *     The request names a domain (disease/family/mechanism) but does
 *     not anchor a specific protein the resolver can verify as
 *     relevant. (Or no domain term at all.)
 *
 *   { status: 'needsConfirmation', candidate, reason, evidence }
 *     The request names a domain AND there is a workspace protein
 *     that IS relevant per local data, but the user has not yet
 *     explicitly confirmed it for this workflow.
 *
 * The classifier is a pure function of (currentMessage, history,
 * workspace). No side effects, no LLM, no network.
 *
 * See labwork/studies/gibbs-clarify-on-ambiguous-target/ for the
 * full design.
 *
 * @module gibbs/text-to-action/anchorResolver
 */

import { diseaseTargets } from '../knowledge/diseaseTargets';
import { matchDisease } from '../knowledge/diseaseMatcher';
import { check as checkRelevance } from './relevanceChecker';

// ─── PDB ID detection ────────────────────────────────────────────────

// PDB IDs are exactly 4 chars — first must be a digit 1-9, the rest
// alphanumeric. Word-boundary so we don't catch 4-char fragments
// embedded in longer tokens like "pdb1abc-mutant".
const PDB_ID_PATTERN = /\b[1-9][a-z0-9]{3}\b/i;

/**
 * @param {string} message
 * @returns {string|null} the PDB ID in upper-case, or null
 * @private
 */
function detectExplicitPdbId(message) {
    if (typeof message !== 'string') return null;
    const m = message.match(PDB_ID_PATTERN);
    return m ? m[0].toUpperCase() : null;
}

// ─── Protein name index (built once at module load) ──────────────────

/**
 * Build a flat index of every known protein name / gene symbol / UniProt
 * appearing in any disease's topTargets. Keys are lower-case for
 * case-insensitive matching.
 *
 * Index entry: { displayName, uniprot, geneSymbol, domains: [slug...] }.
 * One protein may appear across multiple diseases, so we collect domains.
 *
 * @returns {Map<string, Object>}
 * @private
 */
function buildProteinNameIndex() {
    const index = new Map();
    const diseases = (diseaseTargets && diseaseTargets.diseases) || {};

    for (const [slug, entry] of Object.entries(diseases)) {
        if (!entry || !Array.isArray(entry.topTargets)) continue;
        for (const t of entry.topTargets) {
            if (!t) continue;
            const candidates = [];
            if (t.geneSymbol) candidates.push(t.geneSymbol);
            if (t.name) candidates.push(t.name);
            if (t.uniprot) candidates.push(t.uniprot);
            // Add PDB IDs as protein references too — useful when the
            // user names a protein by one of its structures.
            if (Array.isArray(t.pdbIds)) {
                for (const p of t.pdbIds) candidates.push(p);
            }

            for (const raw of candidates) {
                if (typeof raw !== 'string' || raw.length < 2) continue;
                const key = raw.toLowerCase();
                if (!index.has(key)) {
                    index.set(key, {
                        displayName: t.geneSymbol || t.name || t.uniprot,
                        uniprot: t.uniprot,
                        geneSymbol: t.geneSymbol,
                        domains: [slug],
                    });
                } else {
                    const existing = index.get(key);
                    if (!existing.domains.includes(slug)) {
                        existing.domains.push(slug);
                    }
                }
            }
        }
    }
    return index;
}

const PROTEIN_NAME_INDEX = buildProteinNameIndex();

/**
 * Search the message for any known protein name / gene symbol / UniProt
 * with word-boundary matching. Returns the first match found, or null.
 *
 * @param {string} message
 * @returns {Object|null} index entry, plus a `matched` field naming the token
 * @private
 */
function detectExplicitProteinName(message) {
    if (typeof message !== 'string' || !message) return null;
    const lower = message.toLowerCase();

    for (const [key, entry] of PROTEIN_NAME_INDEX) {
        // Word-boundary check: key must be at a token boundary
        const idx = lower.indexOf(key);
        if (idx === -1) continue;
        const before = idx === 0 ? ' ' : lower[idx - 1];
        const after = (idx + key.length >= lower.length)
            ? ' ' : lower[idx + key.length];
        if (/[a-z0-9]/.test(before) || /[a-z0-9]/.test(after)) continue;
        return { ...entry, matched: key };
    }
    return null;
}

// ─── Workspace protein extraction ────────────────────────────────────

/**
 * Given a workspace, surface the loaded protein(s) as candidates that
 * could anchor a workflow. Today we treat the first case data's protein
 * as the candidate; this matches how dock-and-score and other shortcuts
 * behave.
 *
 * @param {Object|null|undefined} workspace
 * @returns {{ pdbId?: string, name?: string }|null}
 * @private
 */
function getWorkspaceCandidate(workspace) {
    if (!workspace) return null;
    if (typeof workspace.firstCaseData !== 'function') return null;
    const cd = workspace.firstCaseData();
    if (!cd) return null;
    const mc = cd.mapCase || (typeof cd.getSolute === 'function' ? cd.getSolute() : null);
    if (!mc) return null;
    return {
        pdbId: mc.pdbId || null,
        name: mc.displayName || mc.name || null,
    };
}

// ─── Cross-turn anchor inheritance ───────────────────────────────────

const DEFAULT_WALK_DEPTH = 5;

/**
 * Walk back through history newest-first and return the most recent
 * domain-term match found in either the userMessage or assistantContent
 * field. Stops at the first match. Bounded by `depth`.
 *
 * @param {Array<Object>} history - conversationHistory entries
 * @param {number} [depth]
 * @returns {string|null} disease slug, or null
 */
export function lookupRecentDomainTerm(history, depth = DEFAULT_WALK_DEPTH) {
    if (!Array.isArray(history) || history.length === 0) return null;
    const slice = history.slice(-Math.max(0, depth));
    for (let i = slice.length - 1; i >= 0; i -= 1) {
        const entry = slice[i];
        if (!entry) continue;

        // Try userMessage first
        if (typeof entry.userMessage === 'string') {
            const m = matchDisease(entry.userMessage);
            if (m && m.slug) return m.slug;
        }
        // Then assistantContent (added 2026-04-28 in commit 68b386b2)
        if (typeof entry.assistantContent === 'string') {
            const m = matchDisease(entry.assistantContent);
            if (m && m.slug) return m.slug;
        }
    }
    return null;
}

// ─── Main classifier ─────────────────────────────────────────────────

/**
 * Classify a workflow-tier request.
 *
 * @param {string} currentMessage - the current-turn user message
 * @param {Array<Object>} [history] - conversationHistory entries (newest last)
 * @param {Object} [workspace] - App.Workspace or test stub
 * @returns {Object} one of:
 *   { status: 'anchored', protein, source }
 *   { status: 'ambiguous', domainTerms[] }
 *   { status: 'needsConfirmation', candidate, reason, evidence }
 */
export function resolve(currentMessage, history, workspace) {
    // 1. Explicit PDB ID in current message — strongest anchor
    const pdbId = detectExplicitPdbId(currentMessage);
    if (pdbId) {
        return {
            status: 'anchored',
            protein: { pdbId, name: null, uniprot: null },
            source: 'pdb_id',
        };
    }

    // 2. Explicit protein-name reference in current message
    const named = detectExplicitProteinName(currentMessage);
    if (named) {
        // If the workspace also has the same protein loaded, prefer
        // anchoring on that (it gives downstream actions concrete
        // case-data references). Otherwise the named match alone is
        // enough.
        const ws = getWorkspaceCandidate(workspace);
        if (ws && ws.name && named.matched
            && ws.name.toLowerCase().includes(named.matched)) {
            return {
                status: 'anchored',
                protein: { pdbId: ws.pdbId, name: ws.name, uniprot: named.uniprot },
                source: 'protein_name',
            };
        }
        return {
            status: 'anchored',
            protein: {
                pdbId: null,
                name: named.displayName,
                uniprot: named.uniprot,
            },
            source: 'protein_name',
        };
    }

    // 3. Domain-term extraction (current turn first, then cross-turn)
    let domainSlug = null;
    const directMatch = matchDisease(currentMessage);
    if (directMatch && directMatch.slug) {
        domainSlug = directMatch.slug;
    } else if (Array.isArray(history) && history.length > 0) {
        domainSlug = lookupRecentDomainTerm(history);
    }

    // 4. Workspace candidate (if any)
    const wsCandidate = getWorkspaceCandidate(workspace);

    // 5. Branch on the (domain, workspace) combination
    if (!domainSlug && !wsCandidate) {
        // No signals at all — ambiguous, no domain terms
        return { status: 'ambiguous', domainTerms: [] };
    }

    if (!domainSlug && wsCandidate) {
        // No domain term but workspace has a protein. The user gave us
        // no domain to verify against — return ambiguous so the
        // routing chain emits a generic clarify rather than silently
        // anchoring on the workspace protein.
        return { status: 'ambiguous', domainTerms: [] };
    }

    if (domainSlug && !wsCandidate) {
        return { status: 'ambiguous', domainTerms: [domainSlug] };
    }

    // 6. Both domain term AND workspace protein — relevance check
    const probe = wsCandidate.pdbId || wsCandidate.name || '';
    const relevance = checkRelevance(probe, domainSlug);
    if (relevance.relevant) {
        return {
            status: 'needsConfirmation',
            candidate: wsCandidate,
            reason: 'workspace_protein_relevant_to_domain',
            evidence: relevance.evidence,
            domainSlug,
        };
    }
    // Workspace protein not relevant to stated domain — emit
    // target-selection clarify, ignoring the workspace.
    return { status: 'ambiguous', domainTerms: [domainSlug] };
}

export default { resolve, lookupRecentDomainTerm };
